<template>
  <div class="summit-home-page">
    <label
      class="input-mode-switch"
      :class="{ 'input-mode-switch--disabled': analyzing }"
      :title="activeTab === 'text' ? '切换为链接输入' : '切换为文本输入'"
    >
      <span class="switch-icon switch-icon--video" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
          <rect x="2.5" y="5.5" width="14" height="13" rx="2.5" stroke="currentColor" stroke-width="1.8" />
          <path d="M16.5 10.2L21 7.5v9l-4.5-2.7v-3.6z" fill="currentColor" />
        </svg>
      </span>
      <span class="switch-icon switch-icon--text" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 4.5h9.5A2.5 2.5 0 0 1 18 7v13.5H8.5A2.5 2.5 0 0 1 6 18V4.5z"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linejoin="round"
          />
          <path d="M9 9.5h6M9 13h6M9 16.5h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </span>
      <input
        type="checkbox"
        class="input"
        :checked="activeTab === 'text'"
        :disabled="analyzing"
        aria-label="切换链接与文本输入"
        @change="onInputModeSwitch"
      />
      <span class="slider"></span>
    </label>

    <header class="hero">
      <h1 class="logo" :key="brandAnimKey">
        <span
          v-for="(char, index) in brandChars"
          :key="`${brandAnimKey}-${index}`"
          class="logo__char"
          :class="{ 'logo__char--hyphen': char === '-' }"
          :style="{ animationDelay: `${index * 0.055}s` }"
        >{{ char }}</span>
      </h1>
      <p
        class="subtitle summit-tagline-loader"
        :key="`subtitle-${brandAnimKey}`"
        role="status"
        :aria-label="taglineLines[0]"
      >
        <span class="summit-tagline-loader__words" aria-hidden="true">
          <span
            v-for="(line, index) in taglineRotateLines"
            :key="`tagline-${brandAnimKey}-${index}`"
            class="summit-tagline-loader__word"
          >{{ line }}</span>
        </span>
        <span class="summit-tagline-loader__sr">{{ taglineLines.join('。') }}</span>
      </p>
    </header>

    <div
      class="input-flip"
      :class="{
        'input-flip--text': activeTab === 'text',
        'input-flip--disabled': analyzing
      }"
    >
      <div class="input-flip__inner">
        <div
          class="input-flip__face input-flip__face--front"
          :aria-hidden="activeTab !== 'video'"
        >
          <div
            class="summit-poda summit-poda--block summit-poda--video"
            :class="{ 'summit-poda--disabled': analyzing }"
          >
            <div class="summit-poda__main">
              <input
                v-model="videoUrl"
                type="text"
                class="summit-poda__input"
                placeholder="粘贴视频分享链接..."
                :disabled="analyzing"
                autocomplete="off"
                spellcheck="false"
                tabindex="0"
                @keyup.enter="onVideoInputEnter"
              />
              <div class="summit-poda__field-icon summit-poda__field-icon--left" aria-hidden="true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                  stroke-linecap="round"
                >
                  <circle r="8" cy="11" cx="11"></circle>
                  <line y2="16.65" y1="22" x2="16.65" x1="22"></line>
                </svg>
              </div>
              <button
                v-if="showVideoClear"
                type="button"
                class="summit-poda__clear"
                aria-label="清除链接"
                @click="clearVideoUrl"
              >
                <i class="el-icon-circle-close"></i>
              </button>
              <button
                type="button"
                class="summit-poda__action"
                :class="{ 'summit-poda__action--ready': canSubmit && !analyzing }"
                aria-label="开始总结"
                :disabled="analyzing || !canSubmit"
                @click="handleStartSummary"
              >
                <i v-if="analyzing" class="el-icon-loading"></i>
                <i v-else class="el-icon-video-camera"></i>
              </button>
            </div>
            <ul class="summit-channels" aria-label="支持的视频渠道">
              <li
                v-for="channel in videoChannels"
                :key="channel.id"
                class="summit-channel"
              >
                <span
                  class="summit-channel__dot"
                  :style="{ background: channel.color }"
                  aria-hidden="true"
                ></span>
                {{ channel.name }}
              </li>
            </ul>
          </div>
        </div>

        <div
          class="input-flip__face input-flip__face--back"
          :aria-hidden="activeTab !== 'text'"
        >
          <div
            class="summit-poda summit-poda--block summit-poda--text"
            :class="{ 'summit-poda--disabled': analyzing }"
          >
            <div class="summit-poda__main">
              <textarea
                v-model="textContent"
                class="summit-poda__input summit-poda__textarea"
                placeholder="请输入需要总结的文本内容..."
                :disabled="analyzing"
                :maxlength="textMaxLength"
                spellcheck="false"
              ></textarea>
              <div class="summit-poda__count" aria-live="polite">
                {{ textContentLength }}/{{ textMaxLength }}
              </div>
              <button
                v-if="showTextClear"
                type="button"
                class="summit-poda__clear"
                aria-label="清除文本"
                @click="clearTextContent"
              >
                <i class="el-icon-circle-close"></i>
              </button>
              <button
                type="button"
                class="summit-poda__action"
                :class="{ 'summit-poda__action--ready': canSubmit && !analyzing }"
                aria-label="开始总结"
                :disabled="analyzing || !canSubmit"
                @click="handleStartSummary"
              >
                <i v-if="analyzing" class="el-icon-loading"></i>
                <svg
                  v-else
                  class="summit-poda__action-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                  stroke-linecap="round"
                  aria-hidden="true"
                >
                  <path d="M7 3.5h8.5L19 7v13.5H7z" />
                  <path d="M15.5 3.5V7H19" />
                  <path d="M10 11h6M10 14.5h6M10 18h4" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="analyzing && analyzingTab" class="summary-stage-wrap">
      <ol
        class="summary-stage-list"
        :aria-label="analyzingTab === 'video' ? '视频总结进度' : '文本总结进度'"
      >
        <li
          v-for="(label, i) in currentStageLabels"
          :key="i"
          class="summary-stage-item"
          :class="{
            'is-done': i < currentStageIndex,
            'is-active': i === currentStageIndex,
            'is-todo': i > currentStageIndex
          }"
        >
          <span class="summary-stage-rail" aria-hidden="true">
            <span class="summary-stage-dot" :class="{ 'summary-stage-dot--done': i < currentStageIndex }">
              <i v-if="i < currentStageIndex" class="el-icon-check summary-stage-dot__check" />
            </span>
            <span v-if="i < currentStageLabels.length - 1" class="summary-stage-line" />
          </span>
          <span class="summary-stage-text">{{
            i < currentStageIndex ? stageDoneCaption(label) : label
          }}</span>
        </li>
      </ol>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { getApiErrorMessage } from '@/utils/apiErrorMessage'

export default {
  name: 'SummitHome',
  data() {
    return {
      brandAnimKey: 0,
      brandAnimReady: false,
      /** video=链接输入，text=文本输入；右上角开关切换 */
      activeTab: 'video',
      videoUrl: '',
      videoChannels: [
        { id: 'douyin', name: '抖音', color: '#111111' },
        { id: 'xhs', name: '小红书', color: '#ff2442' },
        { id: 'bilibili', name: 'B站', color: '#00a1d6' },
        { id: 'kuaishou', name: '快手', color: '#ff4906' },
        { id: 'channels', name: '视频号', color: '#07c160' }
      ],
      textContent: '',
      textMaxLength: 5000,
      analyzing: false,
      /** 发起总结时的 Tab，与当前 activeTab 解耦，避免切换 Tab 时进度条串台 */
      analyzingTab: null,
      userId: null,
      /** 视频总结四阶段：与请求/轮询节奏对齐 */
      videoStageIndex: 0,
      videoStageLabels: [
        '正在解析分享链接',
        '正在获取视频链接',
        '正在解析视频内容',
        '正在总结视频内容'
      ],
      /** 文本总结两阶段 */
      textStageIndex: 0,
      textStageLabels: ['正在读取输入文本', '正在总结文本内容'],
      /** 副标题轮播：来自 InsightPod 品牌报告的主 Slogan / 功能句 */
      taglineLines: [
        '让收藏，用得上',
        '收藏变成知识卡，需要时问一问',
        '别再让收藏吃灰',
        '以前看过的方法，今天怎么用'
      ]
    }
  },
  computed: {
    taglineRotateLines() {
      const lines = this.taglineLines
      if (!lines.length) return []
      return [...lines, lines[0]]
    },
    brandChars() {
      return 'InsightPod'.split('')
    },
    canSubmit() {
      if (this.activeTab === 'video') {
        return !!this.videoUrl.trim()
      }
      return !!this.textContent.trim()
    },
    showVideoClear() {
      return this.activeTab === 'video' && !!this.videoUrl.trim() && !this.analyzing
    },
    showTextClear() {
      return this.activeTab === 'text' && !!this.textContent.trim() && !this.analyzing
    },
    textContentLength() {
      return this.textContent.length
    },
    currentStageLabels() {
      if (this.analyzingTab === 'text') return this.textStageLabels
      return this.videoStageLabels
    },
    currentStageIndex() {
      if (this.analyzingTab === 'text') return this.textStageIndex
      return this.videoStageIndex
    },
    currentStageTitle() {
      const labels = this.currentStageLabels
      const len = labels.length
      const i = this.currentStageIndex
      if (!len) return ''
      if (i >= len) {
        return this.stageDoneCaption(labels[len - 1])
      }
      return labels[i] || ''
    }
  },
  mounted() {
    this.userId = localStorage.getItem('userId') || null
    if (!this.userId) {
      this.$message.error('请先登录')
    }
  },
  activated() {
    if (this.brandAnimReady) {
      this.replayBrandAnim()
    } else {
      this.brandAnimReady = true
    }
  },
  methods: {
    replayBrandAnim() {
      this.brandAnimKey += 1
    },
    onInputModeSwitch(event) {
      if (this.analyzing) return
      this.activeTab = event.target.checked ? 'text' : 'video'
    },
    onVideoInputEnter() {
      if (this.activeTab !== 'video' || this.analyzing || !this.canSubmit) return
      this.handleStartSummary()
    },
    clearTextContent() {
      this.textContent = ''
      this.$nextTick(() => {
        const ta = this.$el.querySelector('.summit-poda__textarea')
        if (ta) ta.focus()
      })
    },
    clearVideoUrl() {
      this.videoUrl = ''
      this.$nextTick(() => {
        const input = this.$el.querySelector('.summit-poda__input')
        if (input) input.focus()
      })
    },
    /** 已完成步骤：去掉「正在」前缀后接「已完成」 */
    stageDoneCaption(label) {
      const s = String(label).trim()
      const core = /^正在/.test(s) ? s.replace(/^正在/, '') : s
      return `${core}已完成`
    },
    /** 跳转详情前极短停顿，避免界面闪断 */
    async finishProgress() {
      await new Promise((resolve) => setTimeout(resolve, 180))
    },
    resetProgress() {
      this.videoStageIndex = 0
      this.textStageIndex = 0
    },
    /** 进行中为 0…n-1；全部完成时为 n（与节点数一致，用于「全部已勾选」） */
    setVideoStage(index) {
      const cap = this.videoStageLabels.length
      this.videoStageIndex = Math.max(0, Math.min(cap, index))
    },
    setTextStage(index) {
      const cap = this.textStageLabels.length
      this.textStageIndex = Math.max(0, Math.min(cap, index))
    },
    unwrapResponseData(raw) {
      if (raw && typeof raw === 'object' && raw.data != null && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
        return raw.data
      }
      return raw && typeof raw === 'object' ? raw : {}
    },
    extractSubmitTaskId(responseData) {
      if (responseData == null) return null
      if (typeof responseData === 'string' || typeof responseData === 'number') return String(responseData)
      if (typeof responseData !== 'object') return null
      if (responseData.taskId != null) return String(responseData.taskId)
      const nested = responseData.data
      if (typeof nested === 'string' || typeof nested === 'number') return String(nested)
      if (nested && typeof nested === 'object' && nested.taskId != null) return String(nested.taskId)
      return null
    },
    normalizeResult(data) {
      return {
        idEzTextSummary: data.idEzTextSummary || data.id || '',
        theme: data.theme || '',
        label: Array.isArray(data.labels) ? data.labels : [],
        summary: data.summarizedText || data.summary || '',
        noteContent: data.noteContent || '',
        parentLabel: data.parentLabel || '',
        parentLabelCode: data.parentLabelCode || ''
      }
    },
    async toDetail(payload) {
      await this.finishProgress()
      const detailPayload = {
        ...payload,
        createdAt: Date.now()
      }
      sessionStorage.setItem('summit-detail-payload', JSON.stringify(detailPayload))
      const rid = detailPayload?.result?.idEzTextSummary ?? detailPayload?.result?.id
      this.$router.push({
        path: '/summit/detail',
        query: rid != null && String(rid).trim() !== '' ? { id: String(rid).trim() } : {}
      })
    },
    /**
     * 接口已成功：先把当前阶段之后未展示的节点依次置为完成，再进详情（避免「有结果仍卡在中间态」）。
     */
    async navigateToDetailAfterFullCompletion(payload) {
      const video = this.analyzingTab === 'video'
      const max = video ? this.videoStageLabels.length : this.textStageLabels.length
      let idx = video ? this.videoStageIndex : this.textStageIndex
      const stepDelayMs = 260
      const tailHoldMs = 420
      while (idx < max) {
        idx += 1
        if (video) this.videoStageIndex = idx
        else this.textStageIndex = idx
        await this.$nextTick()
        if (idx < max) {
          await new Promise((resolve) => setTimeout(resolve, stepDelayMs))
        }
      }
      await new Promise((resolve) => setTimeout(resolve, tailHoldMs))
      await this.toDetail(payload)
    },
    async handleStartSummary() {
      if (!this.userId) {
        this.$message.error('请先登录')
        return
      }
      if (!this.canSubmit || this.analyzing) return
      this.analyzing = true
      this.analyzingTab = this.activeTab
      if (this.activeTab === 'video') {
        this.videoStageIndex = 0
      } else {
        this.textStageIndex = 0
      }

      try {
        if (this.activeTab === 'video') {
          await this.submitVideoSummary()
        } else {
          await this.submitTextSummary()
        }
      } catch (error) {
        this.resetProgress()
        this.$message.error(getApiErrorMessage(error, '请求失败，请重试'))
      } finally {
        this.analyzing = false
        this.analyzingTab = null
      }
    },
    async submitTextSummary() {
      this.setTextStage(0)
      await this.$nextTick()
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      this.setTextStage(1)
      const response = await axios.post('/api/app/core/text-summary/add', {
        originalText: this.textContent.trim(),
        modelType: 'deepseek'
      })

      const data = this.unwrapResponseData(response.data)
      await this.navigateToDetailAfterFullCompletion({
        sourceType: 'text',
        inputValue: this.textContent.trim(),
        result: this.normalizeResult(data)
      })
    },
    async submitVideoSummary() {
      this.setVideoStage(0)
      const response = await axios.post('/api/app/core/dousnap/video/url/analysis', {
        shortUrl: this.videoUrl.trim()
      })
      this.setVideoStage(1)
      const payload = response.data || {}
      const taskId = this.extractSubmitTaskId(payload.taskId != null ? payload.taskId : payload)
      if (!taskId) {
        throw new Error('未获取到任务ID')
      }
      const result = await this.pollVideoTask(taskId)
      await this.navigateToDetailAfterFullCompletion({
        sourceType: 'video',
        inputValue: this.videoUrl.trim(),
        result
      })
    },
    pollVideoTask(taskId) {
      const stageByStatus = {
        PROCESSING: 2,
        SUMMARIZING: 3
      }
      return new Promise((resolve, reject) => {
        const fetchStatus = async () => {
          try {
            const response = await axios.get(`/api/app/core/dousnap/video/task/status/${taskId}`)
            const data = response.data || {}
            const status = data.status
            if (status === 'PENDING') {
              if (this.videoStageIndex < 1) this.setVideoStage(1)
            } else if (Object.prototype.hasOwnProperty.call(stageByStatus, status)) {
              this.setVideoStage(stageByStatus[status])
            }

            if (status === 'COMPLETED') {
              this.setVideoStage(this.videoStageLabels.length)
              await this.$nextTick()
              const inner = data.data && typeof data.data === 'object' ? data.data : {}
              resolve(this.normalizeResult({ ...data, ...inner }))
              return
            }
            if (status === 'FAILED') {
              reject(
                new Error(
                  data.errorMessage || data.msg || data.message || data.errorMsg || '任务执行失败'
                )
              )
              return
            }
            const waitMs = status === 'SUMMARIZING' ? 400 : 10000
            setTimeout(fetchStatus, waitMs)
          } catch (error) {
            reject(error)
          }
        }
        fetchStatus()
      })
    }
  }
}
</script>

<style scoped>
.summit-home-page {
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fc;
  background-image:
    radial-gradient(ellipse 70% 45% at 15% 72%, rgba(66, 83, 255, 0.08) 0%, transparent 52%),
    radial-gradient(ellipse 60% 40% at 88% 28%, rgba(255, 180, 120, 0.07) 0%, transparent 50%);
  padding: calc(clamp(82px, calc(9vh + 40px), 128px) + 30px) 24px
    calc(24px + var(--bottom-tab-height, 72px) + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  max-width: 860px;
  margin: 0 auto;
  width: 100%;
}

.input-mode-switch {
  font-size: 17px;
  position: absolute;
  top: max(12px, env(safe-area-inset-top, 0px));
  right: max(16px, env(safe-area-inset-right, 0px));
  z-index: 20;
  display: inline-block;
  width: 78px;
  height: 34px;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.input-mode-switch .input {
  opacity: 0;
  width: 0;
  height: 0;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}

.input-mode-switch .slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #4253ff;
  transition: 0.4s;
  border-radius: 30px;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}

.input-mode-switch .slider:before {
  position: absolute;
  content: "";
  height: 30px;
  width: 30px;
  border-radius: 20px;
  left: 2px;
  bottom: 2px;
  z-index: 2;
  background-color: #e8e8e8;
  transition: 0.4s;
}

.input-mode-switch .switch-icon svg {
  position: absolute;
  z-index: 1;
  width: 20px;
  height: 20px;
}

.input-mode-switch .switch-icon--video {
  color: #ffffff;
}

.input-mode-switch .switch-icon--video svg {
  top: 5px;
  left: 48px;
  width: 24px;
  height: 24px;
}

.input-mode-switch .switch-icon--text {
  color: #ffffff;
}

.input-mode-switch .switch-icon--text svg {
  top: 5px;
  left: 6px;
  width: 24px;
  height: 24px;
}

.input-mode-switch .input:checked + .slider {
  background-color: #e07a24;
}

.input-mode-switch .input:focus + .slider,
.input-mode-switch .input:focus-visible + .slider,
.input-mode-switch:focus,
.input-mode-switch:focus-within,
.input-mode-switch:active {
  outline: none;
  box-shadow: none;
}

.input-mode-switch .input:checked + .slider:before {
  transform: translateX(44px);
}

.input-mode-switch--disabled {
  opacity: 0.55;
  pointer-events: none;
}

.hero {
  margin-top: -20px;
  margin-bottom: clamp(48px, calc(5vw + 20px), 56px);
  text-align: center;
}

.logo {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: baseline;
  gap: 0;
  margin: 0;
  font-size: clamp(34px, 4vw, 42px);
  font-weight: 800;
  letter-spacing: 0.12em;
  line-height: 1.15;
  overflow: hidden;
  animation: summitBrandGlow 0.55s ease-in-out 0.92s 1 both;
}

.logo::after {
  content: "";
  position: absolute;
  top: -30%;
  left: -45%;
  width: 38%;
  height: 160%;
  background: linear-gradient(
    105deg,
    transparent 0%,
    rgba(255, 255, 255, 0.05) 35%,
    rgba(255, 255, 255, 0.92) 50%,
    rgba(255, 255, 255, 0.05) 65%,
    transparent 100%
  );
  transform: skewX(-22deg);
  animation: summitBrandSweep 0.95s ease-in-out 0.72s forwards;
  pointer-events: none;
  mix-blend-mode: soft-light;
}

.logo__char {
  display: inline-block;
  font-weight: 800;
  opacity: 0;
  transform: translateY(0.35em);
  color: #4253ff;
  text-shadow: 0 0 0 rgba(66, 83, 255, 0.35);
  animation: summitBrandChar 0.42s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.logo__char--hyphen {
  margin: 0 0.02em;
  font-weight: 800;
  opacity: 0;
  animation: summitBrandChar 0.38s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes summitBrandChar {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes summitBrandSweep {
  0% {
    left: -45%;
    opacity: 0;
  }
  12% {
    opacity: 1;
  }
  100% {
    left: 125%;
    opacity: 0;
  }
}

@keyframes summitBrandGlow {
  0%,
  100% {
    filter: brightness(1) drop-shadow(0 0 0 rgba(66, 83, 255, 0));
  }
  50% {
    filter: brightness(1.14) drop-shadow(0 0 16px rgba(66, 83, 255, 0.38));
  }
}

.subtitle {
  margin: clamp(10px, 2.5vw, 14px) 0 0;
  color: #000000;
  font-size: clamp(14px, 1.8vw, 18px);
  line-height: 1.5;
  opacity: 0;
  transform: translateY(0.5rem);
  animation: summitBrandTagline 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.62s forwards;
}

@keyframes summitBrandTagline {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.summit-tagline-loader {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  min-height: calc(1.75em + 4px);
}

.summit-tagline-loader__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.summit-tagline-loader__words {
  --tagline-line-height: 1.75;
  overflow: hidden;
  position: relative;
  height: calc(1em * var(--tagline-line-height));
  line-height: var(--tagline-line-height);
  max-width: 100%;
  padding-inline: 4px;
  box-sizing: content-box;
}

.summit-tagline-loader__word {
  display: block;
  height: calc(1em * var(--tagline-line-height));
  line-height: var(--tagline-line-height);
  color: #e07a24;
  white-space: nowrap;
  animation: summitTaglineSpin 7s infinite;
}

@keyframes summitTaglineSpin {
  10% {
    transform: translateY(-102%);
  }
  25% {
    transform: translateY(-100%);
  }
  35% {
    transform: translateY(-202%);
  }
  50% {
    transform: translateY(-200%);
  }
  60% {
    transform: translateY(-302%);
  }
  75% {
    transform: translateY(-300%);
  }
  85% {
    transform: translateY(-402%);
  }
  100% {
    transform: translateY(-400%);
  }
}

.card {
  background: #f2f6ff;
  border: 1px solid #dfe8ff;
  border-radius: 18px;
}

.summit-workspace {
  margin-top: clamp(20px, 4vw, 28px);
  padding: 8px 8px 8px;
  overflow: hidden;
}

/* 输入框卡片翻转 */
.input-flip {
  position: relative;
  width: 100%;
  margin-top: clamp(20px, 4vw, 28px);
  height: 118px;
  perspective: 1400px;
  perspective-origin: center center;
  transition: height 0.55s cubic-bezier(0.4, 0.15, 0.2, 1);
  z-index: 1;
}

.input-flip--text {
  height: 248px;
}

.input-flip__inner {
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.65s cubic-bezier(0.4, 0.15, 0.2, 1);
}

.input-flip--text .input-flip__inner {
  transform: rotateY(180deg);
}

.input-flip__face {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform: translateZ(0);
}

.input-flip__face--front {
  z-index: 2;
  pointer-events: auto;
}

.input-flip__face--back {
  z-index: 1;
  transform: rotateY(180deg);
  pointer-events: none;
}

.input-flip--text .input-flip__face--front {
  pointer-events: none;
}

.input-flip--text .input-flip__face--back {
  pointer-events: auto;
}

.input-flip--disabled {
  pointer-events: none;
}

.input-flip .summit-poda--block {
  width: 100%;
  height: 100%;
  margin-top: 0;
  padding: 12px 6px 14px;
  box-sizing: border-box;
}

.input-flip .summit-poda {
  height: 100%;
}

.input-flip .summit-poda--video {
  display: flex;
  flex-direction: column;
}

.input-flip .summit-poda--video .summit-poda__main {
  height: 58px;
  flex: 0 0 58px;
}

.summit-channels {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin: 8px 4px 0;
  padding: 0;
  list-style: none;
}

.summit-channel {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: #f5f7fb;
  color: #475467;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.02em;
}

.summit-channel__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex: 0 0 6px;
}

.input-flip .summit-poda--text,
.input-flip .summit-poda--text .summit-poda__main,
.input-flip .summit-poda--text .summit-poda__textarea {
  min-height: 0;
  height: 100%;
}

.input-flip .summit-poda--text .summit-poda__textarea {
  box-sizing: border-box;
}

@media (prefers-reduced-motion: reduce) {
  .input-flip,
  .input-flip__inner {
    transition: none;
  }
}

.summit-poda--block {
  width: 100%;
  margin-top: clamp(20px, 4vw, 28px);
  padding: 12px 6px 14px;
  box-sizing: border-box;
}

.summit-poda--disabled {
  opacity: 0.72;
  pointer-events: none;
}

.summit-poda {
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 58px;
  overflow: visible;
}

.summit-poda__main {
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #eceef3;
  box-shadow: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.summit-poda:focus-within .summit-poda__main {
  border-color: #eceef3;
  /* 描边由内向外逐渐变浅 */
  box-shadow:
    0 0 0 1px rgba(236, 238, 243, 0.95),
    0 0 0 3px rgba(236, 238, 243, 0.55),
    0 0 0 5px rgba(236, 238, 243, 0.28),
    0 0 0 7px rgba(236, 238, 243, 0.1);
}

.summit-poda__input {
  position: relative;
  z-index: 2;
  background-color: transparent;
  border: none;
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: 11px;
  /* Safari/iOS 表单控件优先吃 -webkit-text-fill-color，只写 color 常不生效 */
  color: #000 !important;
  -webkit-text-fill-color: #000 !important;
  caret-color: #000;
  color-scheme: light;
  opacity: 1;
  padding-inline: 52px 92px;
  font-size: clamp(14px, 1.8vw, 16px);
  font-family: inherit;
  line-height: 1.55;
  box-sizing: border-box;
}

.summit-poda__input::placeholder {
  color: #000 !important;
  -webkit-text-fill-color: #000 !important;
  opacity: 1;
}

.summit-poda__input::-webkit-input-placeholder {
  color: #000 !important;
  -webkit-text-fill-color: #000 !important;
  opacity: 1;
}

.summit-poda__input::-moz-placeholder {
  color: #000 !important;
  opacity: 1;
}

.summit-poda__input:-ms-input-placeholder {
  color: #000 !important;
}

.summit-poda__input:focus {
  outline: none;
  color: #000 !important;
  -webkit-text-fill-color: #000 !important;
}

.summit-poda__field-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 3;
  color: #98a2b3;
}

.summit-poda__field-icon--left {
  left: 16px;
}

.summit-poda__clear {
  position: absolute;
  right: 52px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
  opacity: 0.72;
  transition: opacity 0.2s ease, color 0.2s ease, background-color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.summit-poda__clear:hover {
  opacity: 1;
  color: #475467;
  background: rgba(0, 0, 0, 0.05);
}

.summit-poda__clear:active {
  transform: translateY(-50%) scale(0.92);
}

.summit-poda__clear i {
  font-size: 18px;
}

.summit-poda__action {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: #4253ff;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: 0.45;
  transition: opacity 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.summit-poda__action--ready {
  opacity: 1;
  box-shadow: 0 4px 14px rgba(66, 83, 255, 0.35);
}

.summit-poda__action--ready:active {
  transform: translateY(-50%) scale(0.94);
}

.summit-poda__action:disabled {
  cursor: not-allowed;
}

.summit-poda__action i {
  font-size: 18px;
}

@media (prefers-color-scheme: dark) {
  .summit-poda__action {
    background: #5a6aff;
  }

  .summit-poda__action--ready {
    box-shadow: 0 4px 14px rgba(90, 106, 255, 0.4);
  }
}

/* 文本输入：白底灰边，保留多行高度 */
.summit-poda--text {
  height: auto;
  min-height: 220px;
}

.summit-poda--text .summit-poda__main {
  border-radius: 14px;
  min-height: 220px;
}

.summit-poda--text .summit-poda__textarea {
  display: block;
  width: 100%;
  height: auto;
  min-height: 220px;
  padding: 16px 52px 52px 18px;
  resize: none;
  line-height: 1.55;
  font-family: inherit;
  overflow-y: auto;
}

.summit-poda--text .summit-poda__clear {
  top: auto;
  bottom: 12px;
  transform: none;
}

.summit-poda--text .summit-poda__clear:active {
  transform: scale(0.92);
}

.summit-poda--text .summit-poda__action {
  top: auto;
  bottom: 10px;
  transform: none;
}

.summit-poda--text .summit-poda__action--ready:active {
  transform: scale(0.94);
}

.summit-poda--text .summit-poda__action-icon {
  display: block;
}

.summit-poda--text .summit-poda__count {
  position: absolute;
  left: 16px;
  bottom: 14px;
  z-index: 3;
  font-size: 12px;
  line-height: 1;
  color: #98a2b3;
  pointer-events: none;
  user-select: none;
  font-variant-numeric: tabular-nums;
}

.summit-workspace__tabs {
  display: grid;
  gap: 6px;
  grid-template-columns: 1fr 1fr;
  position: relative;
  z-index: 2;
}

.summit-workspace__body {
  position: relative;
  z-index: 1;
  margin-top: 0;
  padding: clamp(12px, 2.5vw, 14px);
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #dfe8ff;
}

.summit-workspace--video .summit-workspace__body {
  border-top-left-radius: 0;
}

.summit-workspace--text .summit-workspace__body {
  border-top-right-radius: 0;
}

.tab-btn {
  border: 1px solid transparent;
  background: transparent;
  height: 46px;
  border-radius: 12px;
  color: #536d98;
  font-size: clamp(14px, 1.7vw, 16px);
  font-weight: 500;
  cursor: pointer;
  position: relative;
  overflow: visible;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.tab-btn i {
  margin-right: 6px;
  font-size: 15px;
}

.tab-btn.active {
  background: #ffffff;
  color: #3648ff;
  border-color: #dfe8ff;
  border-bottom-color: #ffffff;
  border-radius: 12px 12px 0 0;
  box-shadow: none;
  z-index: 3;
}

.tab-btn.active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  background: #ffffff;
}

.tab-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.tab-btn:focus:not(.active),
.tab-btn:focus-visible:not(.active),
.tab-btn:active:not(.active) {
  outline: none;
  border-radius: 12px;
}

.text-input-block {
  display: flex;
  flex-direction: column;
  gap: 0;
  border-radius: 10px;
  overflow: hidden;
  background: #f8faff;
  border: 1px solid #eef2fb;
}

.text-input-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 10px 6px 12px;
  border-bottom: 1px solid #eef0f4;
  background: #f8faff;
}

.text-input-clear-btn {
  color: #909399 !important;
  padding: 4px 8px !important;
}

.text-input-clear-btn:not(:disabled):hover {
  color: #4f54ff !important;
}

.input-area /deep/ .el-input__inner,
.text-area /deep/ .el-textarea__inner {
  border: 1px solid #eef2fb;
  border-radius: 10px;
  background: #f8faff;
  font-size: clamp(14px, 1.8vw, 16px);
  color: #6f84b6;
}

.input-area /deep/ .el-input__inner:focus,
.text-area /deep/ .el-textarea__inner:focus {
  border-color: #c5cffd;
  background: #ffffff;
}

.input-area /deep/ .el-input__inner {
  height: 48px;
}

.text-input-block .text-area /deep/ .el-textarea__inner {
  border-radius: 0 0 12px 12px !important;
}

.text-area /deep/ .el-textarea__inner {
  padding: 12px;
  min-height: 180px;
  line-height: 1.6;
}

.start-btn {
  width: 100%;
  margin-top: clamp(16px, 3vw, 20px);
  height: 48px;
  border-radius: 12px;
  font-size: clamp(15px, 1.9vw, 18px);
  font-weight: 600;
  background: #aebcff;
  border-color: #aebcff;
  transition: background-color 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;
}

.start-btn /deep/ i {
  margin-right: 6px;
  font-size: 16px;
}

.start-btn.is-ready {
  background: #4f54ff !important;
  border-color: #4f54ff !important;
  box-shadow: 0 8px 20px rgba(79, 84, 255, 0.26);
}

.start-btn--busy {
  white-space: normal;
  line-height: 1.35;
  padding-top: 10px;
  padding-bottom: 10px;
  height: auto;
  min-height: 48px;
}

.start-btn__stage-label {
  display: inline-block;
  max-width: 100%;
  text-align: center;
  font-size: clamp(13px, 3.6vw, 15px);
  font-weight: 600;
  vertical-align: middle;
}

.summary-stage-wrap {
  margin-top: 12px;
  padding: 12px 14px 10px;
  border-radius: 12px;
  background: #f8faff;
  border: 1px solid rgba(79, 84, 255, 0.1);
  box-shadow: none;
}

.summary-stage-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.summary-stage-item {
  display: flex;
  align-items: stretch;
  gap: 10px;
  min-height: 26px;
}

.summary-stage-item + .summary-stage-item {
  margin-top: 2px;
}

.summary-stage-rail {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 18px;
  flex-shrink: 0;
}

.summary-stage-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
  box-sizing: border-box;
  border: 2px solid #c8d4ef;
  background: #fff;
  transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease, width 0.2s ease, height 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-stage-dot--done {
  width: 16px;
  height: 16px;
  margin-top: 3px;
  border: none;
  background: #22a06b;
  box-shadow: 0 0 0 2px rgba(34, 160, 107, 0.28);
}

.summary-stage-dot__check {
  font-size: 10px;
  color: #ffffff;
  line-height: 1;
  font-weight: 700;
}

.summary-stage-line {
  flex: 1;
  width: 2px;
  min-height: 12px;
  margin: 2px 0 0;
  border-radius: 1px;
  background: #e2e9f7;
  transition: background 0.25s ease;
}

.summary-stage-text {
  flex: 1;
  padding: 2px 0 10px;
  font-size: 12px;
  line-height: 1.5;
  color: #8a9bc0;
  transition: color 0.25s ease;
}

.summary-stage-item.is-done .summary-stage-line {
  background: linear-gradient(180deg, #22a06b 0%, #3ecf8e 100%);
}

.summary-stage-item.is-done .summary-stage-text {
  color: #2d6a4a;
}

.summary-stage-item.is-active .summary-stage-dot {
  border-color: #4f54ff;
  background: #eef0ff;
  box-shadow: 0 0 0 3px rgba(79, 84, 255, 0.28);
  animation: summary-stage-pulse 1.25s ease-in-out infinite;
}

.summary-stage-item.is-active .summary-stage-text {
  color: #2c4774;
  font-weight: 600;
}

.summary-stage-item.is-todo .summary-stage-dot {
  border-color: #dce4f5;
  background: #f7f9fc;
}

@keyframes summary-stage-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 3px rgba(79, 84, 255, 0.22);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(79, 84, 255, 0.12);
  }
}

@media (prefers-reduced-motion: reduce) {
  .summary-stage-item.is-active .summary-stage-dot {
    animation: none;
  }
}

@media (max-width: 768px) {
  .summit-home-page {
    padding: calc(clamp(68px, calc(7vh + 40px), 92px) + 30px) 16px
      calc(20px + var(--bottom-tab-height, 72px) + env(safe-area-inset-bottom, 0px));
  }

  .hero {
    margin-top: -20px;
    margin-bottom: 44px;
  }

  .summit-workspace {
    margin-top: 12px;
    padding: 6px;
  }

  .summit-workspace__body {
    padding: 10px;
  }

  .tab-btn {
    height: 42px;
    font-size: 14px;
  }

  .tab-btn i {
    font-size: 14px;
  }

  .input-area /deep/ .el-input__inner {
    height: 44px;
    font-size: 14px;
  }

  .input-flip {
    height: 114px;
  }

  .input-flip--text {
    height: 228px;
  }

  .input-flip .summit-poda__input {
    font-size: 14px;
  }

  .text-area /deep/ .el-textarea__inner {
    min-height: 150px;
    font-size: 14px;
  }

  .start-btn {
    height: 44px;
    font-size: 15px;
  }

  .start-btn--busy {
    height: auto;
    min-height: 44px;
  }
}

@media (min-width: 1200px) {
  .summit-home-page {
    padding-top: calc(clamp(96px, calc(10vh + 40px), 150px) + 30px);
  }

  .tab-btn {
    height: 50px;
  }

  .input-area /deep/ .el-input__inner {
    height: 50px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .logo__char,
  .logo__char--hyphen {
    animation: none;
    opacity: 1;
    transform: none;
  }

  .logo::after {
    animation: none;
    display: none;
  }

  .logo {
    animation: none;
  }

  .subtitle {
    animation: none;
    opacity: 1;
    transform: none;
  }

  .summit-tagline-loader__word {
    animation: none;
    transform: none;
  }

  .summit-tagline-loader__word:not(:first-child) {
    display: none;
  }

}
</style>
